import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChipCounterList,
  Column,
  Flex,
  FlexColumn,
  Link,
  OverflowTooltip,
  Table,
} from '@elementary/common-ui/components';
import { ToggleColumnsMenu } from '@elementary/common-ui/components/Table';
import { Test, Totals } from '@elementary/common-ui/types';
import { getTestResultsTotalsColumnFilterConfig } from '@elementary/common-ui/utils/testResultsTotals';
import DescriptionColumnRenderer from 'catalog/components/DescriptionColumnRenderer';
import { STICKY_SECTION_DEPENDENCIES_HEIGHT } from 'catalog/constants';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import ActionsColumnRenderer from 'catalog/components/ActionsColumnRenderer';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import { distinctArray } from '@elementary/common-ui/utils/common';
import TestResultsTableCell from 'shared/cellRenderers/TestResultsTableCell';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import {
  AssetData,
  ColumnAssetResponse,
} from 'shared/hooks/useAssetData/types';
import { Typography } from '@mui/material';
import {
  compareByTestResultsTotals,
  getTestResultsGroup,
  getTestExecutionTotals,
} from 'shared/utils/testExecutions';
import AssetsTableActionMenu from '../AssetsTableActionMenu';

const urlParamsPrefix = 'catalog_asset_table_filters';
const localStorageKey = 'catalog_asset_table';

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.common.white};
  max-height: calc(100vh - ${STICKY_SECTION_DEPENDENCIES_HEIGHT}px) !important;
`;

const ActionMenuWrapper = styled.div`
  position: fixed;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer + 2};
`;

const TableHeader = styled(Flex)`
  justify-content: flex-end;
  padding-bottom: 8px;
`;

const NoColumnsMessageWrapper = styled(FlexColumn)`
  border-radius: 8px;
  padding: 16px;
  margin-top: 32px;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const NoColumnsMessage = () => (
  <NoColumnsMessageWrapper>
    <Typography
      sx={{ fontWeight: 'bold', fontSize: 'inherit', color: 'inherit' }}
    >
      Column information could not be retrieved.
    </Typography>
    <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
      This might happen due to either insufficient permissions or an outdated
      Elementary dbt package.
    </Typography>
    <Link
      style={{ fontSize: 'inherit' }}
      href="https://docs.elementary-data.com/cloud/guides/troubleshoot#column-information-cannot-be-retrieved"
    >
      Click here to learn more.
    </Link>
  </NoColumnsMessageWrapper>
);

type Props = {
  asset?: AssetData;
  test?: Test;
};

const ModelColumnsTable = ({ asset, test }: Props) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [wrapperRect, setWrapperRect] = useState({ left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasPermission = useEnvPermission();

  const modelColumns = useMemo(() => asset?.columns ?? [], [asset?.columns]);

  useViewLoadTracking({
    name: 'Model columns table',
    loadDeps: [asset, modelColumns],
  });

  const { columnsTestResults, columnsTestResultsCounters } = useMemo(() => {
    const results: Record<string, ReturnType<typeof getTestResultsGroup>> = {};
    const counters: Record<string, Totals> = {};
    const executions = asset?.latestTestExecutions ?? [];

    modelColumns.forEach((col) => {
      const columnExecutions = executions.filter(
        (exec) =>
          exec.execution.columnName?.toLowerCase() === col.name.toLowerCase(),
      );
      results[col.name] = getTestResultsGroup(columnExecutions);
      counters[col.name] = getTestExecutionTotals(columnExecutions);
    });

    return {
      columnsTestResults: results,
      columnsTestResultsCounters: counters,
    };
  }, [asset?.latestTestExecutions, modelColumns]);

  const selectedIds = isSelectAll
    ? modelColumns.map((c) => c.id.replace('column.', ''))
    : selectedItems.map((i) => i.replace('column.', ''));

  const chatContext = useMemo(() => {
    if (selectedIds.length === 0) return undefined;
    const selectedColumnNames = modelColumns
      .filter((c) => selectedIds.includes(c.id.replace('column.', '')))
      .map((c) => c.name);
    return {
      assetIds: selectedIds,
      displayLabels: selectedColumnNames,
    };
  }, [selectedIds, modelColumns]);

  useChatContext(chatContext);

  const showAddTestButton =
    hasPermission('EDIT_TEST_CONFIGURATIONS') &&
    asset?.artifactType !== undefined &&
    EDITABLE_ASSET_TYPES.includes(asset.artifactType) &&
    !!asset?.canCreateTests &&
    !test;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const updateRect = () => {
      const rect = el.getBoundingClientRect();
      setWrapperRect({ left: rect.left, width: rect.width });
    };

    updateRect();
    const observer = new ResizeObserver(updateRect);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedItems([]);
    setIsSelectAll(false);
  }, [setSelectedItems, setIsSelectAll]);

  useEffect(() => clearSelections(), [asset, clearSelections]);

  const modelTagColumnsCount = modelColumns.reduce(
    (acc, column) => acc + (column.tags?.length || 0),
    0,
  );

  const columns: Column<ColumnAssetResponse>[] = [
    {
      id: 'name',
      header: 'Column name',
      sortable: true,
      width: '180',
      compareFunc: (a, b) =>
        (a.displayName || a.name).localeCompare(b.displayName || b.name),
      renderer: (val) => (
        <OverflowTooltip>{val.displayName || val.name}</OverflowTooltip>
      ),
    },
    {
      id: 'dataType',
      header: 'Column type',
      sortable: true,
      width: '160',
      renderer: (val) => (
        <OverflowTooltip>{val.dataType.toUpperCase()}</OverflowTooltip>
      ),
    },
    {
      id: 'description',
      header: 'Column description',
      filterable: false,
      sortable: false,
      renderer: (val: any) => (
        <DescriptionColumnRenderer text={val.description} />
      ),
    },
    {
      id: 'tags',
      header: 'Column tags',
      filterable: true,
      defaultUntoggled: !modelTagColumnsCount,
      renderer: ({ tags }) => <ChipCounterList items={tags || []} />,
      filterOptionsOverride: distinctArray(
        modelColumns?.flatMap((i) => i.tags || []),
      )
        .map((t) => ({ label: t, value: t }))
        .concat({ label: '', value: '' }),
    },
    {
      id: 'id',
      header: 'Test results',
      showValueWhenEmpty: true,
      ...getTestResultsTotalsColumnFilterConfig({
        testResultsTotals: columnsTestResultsCounters,
        key: 'name',
      }),
      sortable: true,
      compareFunc: (a, b) =>
        compareByTestResultsTotals(
          columnsTestResultsCounters[a.name],
          columnsTestResultsCounters[b.name],
        ),
      renderer: ({ name }) => (
        <TestResultsTableCell
          assetId={asset?.id ?? ''}
          statuses={columnsTestResults[name]}
          columnName={name}
        />
      ),
      hideColumn: !hasPermission('VIEW_TESTS'),
    },
    {
      id: 'actions',
      header: '',
      filterable: false,
      sortable: false,
      width: 100,
      metaColumn: true,
      nonToggleable: true,
      renderer: ({ id }) => <ActionsColumnRenderer columnId={id} />,
    },
  ];

  return (
    <>
      {asset && modelColumns.length === 0 ? (
        <NoColumnsMessage />
      ) : (
        <TableWrapper ref={wrapperRef}>
          <TableHeader>
            <ToggleColumnsMenu
              columns={columns}
              localStorageKey={localStorageKey}
            />
          </TableHeader>
          <Table
            key={asset?.id}
            idKey="id"
            data={modelColumns}
            columns={columns}
            selectionColumn
            selectedItems={selectedItems}
            isSelectAll={isSelectAll}
            onSelectItem={setSelectedItems}
            onSelectAllChange={setIsSelectAll}
            defaultSortDirection="desc"
            centerRowCell={false}
            localStorageKey={localStorageKey}
            urlParamsPrefix={urlParamsPrefix}
            wrapperSx={{
              height: '100%',
              maxHeight: '100%',
            }}
            bottomScrollPadding={100}
          />

          {createPortal(
            <ActionMenuWrapper
              style={{ left: wrapperRect.left, width: wrapperRect.width }}
            >
              <AssetsTableActionMenu
                isEditable={showAddTestButton}
                options={['test']}
                type="column"
                selectedAssets={selectedIds}
                onClose={clearSelections}
                showWithoutSelection={false}
              />
            </ActionMenuWrapper>,
            document.body,
          )}
        </TableWrapper>
      )}
    </>
  );
};

export default ModelColumnsTable;
